import React, { Fragment, useEffect, useState } from 'react';

import {
    faBalanceScale,
    faCalendarAlt, faPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fab, ToggleButton, ToggleButtonGroup } from '@mui/material';

import {
    ButtonBigDashed,
    ButtonBlueLight, FontIcon,
    ItemOption,
    ItemSelected,
    RequiredSelectHack,
    SelectCreateContainer,
    Text,
    TextNoOptionsAvailable,
    Translate
} from 'components';
import { useModal, useStores } from 'hooks';
import { ModalState } from 'hooks/ModalProvider';
import { observer } from 'mobx-react-lite';
import { EntityView, EquipmentSummaryItem, EquipmentView } from 'models';
import { getIdentifier } from 'models/detail/EquipmentView';
import { CircuitView, RefrigerationUnitView } from 'models/detail/RefrigerationUnitView';
import { isMobileOnly } from 'react-device-detect';
import Media from 'react-media';
import Select from 'react-select';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ToolsService } from 'services';
import styled from 'styled-components';
import { COUNTRY_CODE, TOOL_TYPES } from 'utils/constants';
import {
    DocumentHelper,
    InterventionHelper,
    formatDateString,
    translate
} from 'utils/helpers';
import { Question } from '../Question/Question';

const FLUID_CATEGORIES = {
    HCFC: 'HCFC',
    HFC: 'HFC/PFC',
    HFO: 'HFO',
    PFC: 'HFC/PFC',
    CFC: 'CFC'
};

const FLUID_AMOUNT_KG = {
    NONE: '0',
    LOW: '2kg<Q<30kg',
    MEDIUM: '30kg<Q<300kg',
    HIGH: 'Q>300kg'
};

const FLUID_AMOUNT_TN = {
    NONE: '0',
    LOW: '5t<teqCO2<50t',
    MEDIUM: '50t<teqCO2<500t',
    HIGH: 'teqCO2>500t'
};

const FLUID_CONTROL_FREQUENCY_WITH_DETECTOR = {
    NONE: 'noControlRequired',
    LOW: 'month24',
    MEDIUM: 'month12',
    HIGH: 'month6'
};

const FLUID_CONTROL_FREQUENCY_WITHOUT_DETECTOR = {
    NONE: 'noControlRequired',
    LOW: 'month12',
    MEDIUM: 'month6',
    HIGH: 'month3'
};

const DataContainer = styled.p`
  span {
    font-weight: 600;
  }
`;

const ToolOption = ({
                        tool,
                        toolType,
                        isInsideSelect,
                        ...rest
                    }: {
    isInsideSelect?: boolean
    rest?,
    tool: EquipmentView,
    toolType: string
}) => {
    let icon: JSX.Element;
    if (toolType === TOOL_TYPES.SCALE) {
        icon = <FontAwesomeIcon icon={faBalanceScale} size="lg" style={{ marginRight: '0.5rem', marginLeft: '0.5rem' }} />;
    } else if (toolType === TOOL_TYPES.LEAK_DETECTOR) {
        icon = <FontIcon fontSize="4rem" icon="icon-leak-detector" />;
    } else {
        icon = <></>;
    }

    return (
      <ItemOption {...rest} isInsideSelect={isInsideSelect}>
        {icon}
        <article data-cy="toolSelected">
          <Text fontSize="2rem">{getIdentifier(tool)}</Text>
          {tool.lastControlDate && (
            <p>
              <FontAwesomeIcon icon={faCalendarAlt} />
              {formatDateString(tool.lastControlDate)}
            </p>
                )}
        </article>
      </ItemOption>
    );
};

const ToolSelection = ({
                           deleteAction,
                           editAction,
                           tool,
                           toolType
                       }: {
    deleteAction,
    editAction,
    tool: EquipmentView,
    toolType: string
}) => (
  <Fragment>
    <ItemSelected
            deleteAction={deleteAction} editAction={tool.id ? null : editAction}
        >
      <ToolOption tool={tool} toolType={toolType} />
    </ItemSelected>
  </Fragment>
);

interface FormStateControlView {
    circuit?: CircuitView;
    controlFrequency?: string;
    equipment?: RefrigerationUnitView;
    equipmentOwner?: EntityView;
    fluidAmount?: string;
    fluidCategory?: string;
    leakDetectionSystem?: boolean;
    leakDetector?: EquipmentView;
    scale?: EquipmentView;
}

export const Control = observer(({
                                     countryCode,
                                     form,
                                     name
                                 }: {
    countryCode: string,
    form: UseFormReturn<any>,
    name?: string
}) => {
    const [, forceUpdate] = useState({});

    const { userStore } = useStores();
    const { open, close } = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const [availableLeakDetectors, setAvailableLeakDetectors] = useState([]);
    const [availableScales, setAvailableScales] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const { control, watch, setValue } = form;
    const circuit = watch('circuit');
    const equipment = watch('equipment');
    const leakDetectionSystem = watch('leakDetectionSystem');
    const leakDetector = watch('leakDetector');
    const scale = watch('scale');

    // Create a formValues object to mimic the formState.values from react-use-form-state
    const formValues = {
        circuit,
        equipment,
        leakDetectionSystem,
        leakDetector,
        scale
    };

    const isGB = countryCode === COUNTRY_CODE.GB;
    const isCanada = countryCode === COUNTRY_CODE.CA;
    const isFrance = countryCode === COUNTRY_CODE.FR;

    useEffect(() => {
        if (userStore.isOffline) {
            InterventionHelper.loadLeakDetectorsStored()
                .then(LD => setAvailableLeakDetectors(LD));
            InterventionHelper.loadScalesStored()
                .then(S => setAvailableScales(S));
        } else {
            setIsLoading(true);
            Promise.all([ToolsService.getLeakDetectors(), ToolsService.getScales()])
                .then(([leakDetectors, scales]) => {
                    const mappedLD = DocumentHelper.getDatasWithDocumentsFormatted({ datas: leakDetectors?.content || [] });
                    setAvailableLeakDetectors(mappedLD);
                    const mappedScales = DocumentHelper.getDatasWithDocumentsFormatted({ datas: scales?.content || [] });
                    setAvailableScales(mappedScales);
                })
                .finally(() => setIsLoading(false));
        }
    }, [userStore.isOffline]);

    const isHCFC = () => (
        circuit
        && circuit.fluid
        && circuit.fluid.fluidClass
        && FLUID_CATEGORIES[circuit.fluid.fluidClass] === FLUID_CATEGORIES.HCFC
    );

    const isHFO = () => (
        circuit
        && circuit.fluid
        && circuit.fluid.fluidClass
        && FLUID_CATEGORIES[circuit.fluid.fluidClass] === FLUID_CATEGORIES.HFO
    );

    const teqCO2 = (circuit && circuit.fluid)
        ? Number([
            Number(circuit.fluidCapacity * (circuit.fluid.co2Coef)).toString(),
            Number(circuit.fluidCapacity * (circuit.fluid.co2Coef)).toFixed(2)
        ].reduce((a, b) => (a.length <= b.length ? a : b)))
        : 0;

    useEffect(() => {
        setValue('leakDetectionSystem', leakDetectionSystem || !!equipment?.hasLeakDetectionSystem);
    }, [equipment, leakDetectionSystem, setValue]);

    useEffect(() => {
        // Setup default values
        let fluidAmountIndex = 'NONE';
        let fluidAmountMapping: string;

        if (circuit && circuit.fluid && circuit.fluid.fluidClass) {
            const fluidAmountCapacity = circuit.fluidCapacity;
            // Default fluidCategory
            setValue('fluidCategory', FLUID_CATEGORIES[circuit.fluid.fluidClass]);
            // Default fluidAmount
            if (isHCFC()) {
                if (fluidAmountCapacity >= 2 && fluidAmountCapacity < 30) {
                    fluidAmountMapping = FLUID_AMOUNT_KG.LOW;
                    fluidAmountIndex = 'LOW';
                } else if (fluidAmountCapacity >= 30 && fluidAmountCapacity < 300) {
                    fluidAmountMapping = FLUID_AMOUNT_KG.MEDIUM;
                    fluidAmountIndex = 'MEDIUM';
                } else if (fluidAmountCapacity >= 300) {
                    fluidAmountMapping = FLUID_AMOUNT_KG.HIGH;
                    fluidAmountIndex = 'HIGH';
                } else {
                    fluidAmountMapping = FLUID_AMOUNT_KG.NONE;
                }
                setValue('fluidAmount', fluidAmountMapping);
            } else if (isHFO()) {
                if (fluidAmountCapacity >= 1 && fluidAmountCapacity < 10) {
                    fluidAmountMapping = FLUID_AMOUNT_KG.LOW;
                    fluidAmountIndex = 'LOW';
                } else if (fluidAmountCapacity >= 10 && fluidAmountCapacity < 100) {
                    fluidAmountMapping = FLUID_AMOUNT_KG.MEDIUM;
                    fluidAmountIndex = 'MEDIUM';
                } else if (fluidAmountCapacity >= 100) {
                    fluidAmountMapping = FLUID_AMOUNT_KG.HIGH;
                    fluidAmountIndex = 'HIGH';
                } else {
                    fluidAmountMapping = FLUID_AMOUNT_KG.NONE;
                }
                setValue('fluidAmount', fluidAmountMapping);
            } else {
                if (teqCO2 >= 5 && teqCO2 < 50) {
                    fluidAmountMapping = FLUID_AMOUNT_TN.LOW;
                    fluidAmountIndex = 'LOW';
                } else if (teqCO2 >= 50 && teqCO2 < 500) {
                    fluidAmountMapping = FLUID_AMOUNT_TN.MEDIUM;
                    fluidAmountIndex = 'MEDIUM';
                } else if (teqCO2 >= 500) {
                    fluidAmountMapping = FLUID_AMOUNT_TN.HIGH;
                    fluidAmountIndex = 'HIGH';
                } else {
                    fluidAmountMapping = FLUID_AMOUNT_TN.NONE;
                }
                setValue('fluidAmount', fluidAmountMapping);
            }
        }

        // Default controlFrequency
        if (leakDetectionSystem && !isHCFC()) {
            setValue('controlFrequency', translate(`common.${FLUID_CONTROL_FREQUENCY_WITH_DETECTOR[fluidAmountIndex]}`));
        } else {
            setValue('controlFrequency', translate(`common.${FLUID_CONTROL_FREQUENCY_WITHOUT_DETECTOR[fluidAmountIndex]}`));
        }
    }, [circuit, leakDetectionSystem, setValue]);

    const handleAddScale = form => {
        close();
        setValue('scale', form);
        forceUpdate({});
    };

    const handleAddLeakDetector = form => {
        close();
        setValue('leakDetector', form);
        forceUpdate({});
    };

    const ScaleOption = ({
                             data,
                             innerProps
                         }: {
        data: EquipmentView,
        innerProps
    }) => (
      <ToolOption isInsideSelect tool={data} toolType={TOOL_TYPES.SCALE} {...innerProps} />
    );

    const LeakDetectorOption = ({
                                    data,
                                    innerProps
                                }: {
        data: EquipmentView,
        innerProps
    }) => (
      <ToolOption isInsideSelect tool={data} toolType={TOOL_TYPES.LEAK_DETECTOR} {...innerProps} />
    );

    const CustomFilter: (
        option: {
            data: EquipmentSummaryItem
        },
        search: string
    ) => boolean = (
        option,
        search = ''
    ) => (option.data.model && option.data.model.toLowerCase().includes(search.toLowerCase()))
        || (option.data.brand && option.data.brand.toLowerCase().includes(search.toLowerCase()))
        || (option.data.serialNumber && option.data.serialNumber.toLowerCase().includes(search.toLowerCase()))
        || (option.data.internalIdentification && option.data.internalIdentification.toLowerCase().includes(search.toLowerCase()));

    const displayNoDetectorAvailable = () => (
      <Fragment>
        <TextNoOptionsAvailable>{translate('errors.noTool')}</TextNoOptionsAvailable>
        <ButtonBigDashed
                data-cy="addTool"
                onClick={() => open({
                    type: 'ADD_TOOL',
                    onSubmit: handleAddLeakDetector,
                    defaultValues: {
                        ...new EquipmentView(),
                        model: searchInput,
                        type: {
                            value: TOOL_TYPES.LEAK_DETECTOR,
                            label: translate('common.toolType.LEAK_DETECTOR')
                        }
                    },
                    forIntervention: true,
                    openSecondaryModal: (options: ModalState) => open(options, true)
                })}
            >
          <FontAwesomeIcon icon={faPlus} />
          {translate('button.createTool')}
        </ButtonBigDashed>
      </Fragment>
    );

    const displayNoScaleAvailable = () => (
      <Fragment>
        <TextNoOptionsAvailable>
          {translate('errors.noToolInput', { searchInput })}
        </TextNoOptionsAvailable>
        <ButtonBigDashed
                data-cy="addTool"
                onClick={() => open({
                    type: 'ADD_TOOL',
                    onSubmit: handleAddScale,
                    defaultValues: {
                        ...new EquipmentView(),
                        model: searchInput,
                        type: {
                            value: TOOL_TYPES.SCALE,
                            label: translate('common.toolType.SCALE')
                        }
                    },
                    forIntervention: true,
                    openSecondaryModal: (options: ModalState) => open(options, true)
                })}
            >
          <FontAwesomeIcon icon={faPlus} />
          {translate('button.createTool')}
        </ButtonBigDashed>
      </Fragment>
    );

    function leakDetectorSelector() {
        return !isCanada && (
        <Fragment>
          <Question label={translate('interventionForm.control.questionChooseDetector')} required={isFrance}>
            <SelectCreateContainer>
              <div>
                <Select
                                components={{ Option: LeakDetectorOption }}
                                filterOption={CustomFilter}
                                id="selectLeakDetector"
                                isClearable
                                isLoading={isLoading}
                                isSearchable
                                menuPlacement={isMobileOnly ? 'top' : 'bottom'}
                                noOptionsMessage={displayNoDetectorAvailable}
                                options={availableLeakDetectors}
                                placeholder={translate('common.searchByModelBrand')}
                                value={leakDetector || ''}
                                onChange={handleAddLeakDetector}
                                onInputChange={value => setSearchInput(value)}
                            />
                {
                                isFrance && <RequiredSelectHack value={leakDetector || ''} />
                            }
              </div>
              <div>
                <Media query="(max-width: 768px)">
                  {matches => (matches
                                        ? (
                                          <Fab
                                                color="primary"
                                                size="small"
                                                onClick={() => open({
                                                    type: 'ADD_TOOL',
                                                    onSubmit: handleAddLeakDetector,
                                                    defaultValues: {
                                                        ...new EquipmentView(),
                                                        type: {
                                                            value: TOOL_TYPES.LEAK_DETECTOR,
                                                            label: translate('common.toolType.LEAK_DETECTOR')
                                                        }
                                                    },
                                                    forIntervention: true,
                                                    openSecondaryModal: (options: ModalState) => open(options, true)
                                                })}
                                            >
                                            <FontAwesomeIcon icon={faPlus} />
                                          </Fab>
                                        ) : (
                                          <ButtonBlueLight
                                                data-cy="addLeakDetector"
                                                onClick={() => open({
                                                    type: 'ADD_TOOL',
                                                    onSubmit: handleAddLeakDetector,
                                                    defaultValues: {
                                                        ...new EquipmentView(),
                                                        type: {
                                                            value: TOOL_TYPES.LEAK_DETECTOR,
                                                            label: translate('common.toolType.LEAK_DETECTOR')
                                                        }
                                                    },
                                                    forIntervention: true,
                                                    openSecondaryModal: (options: ModalState) => open(options, true)
                                                })}
                                            >
                                            <FontAwesomeIcon icon={faPlus} />
                                            {translate('button.createLeakDetector')}
                                          </ButtonBlueLight>
                                        )
                                )}
                </Media>
              </div>
            </SelectCreateContainer>
          </Question>
        </Fragment>
        );
    }

    function scaleSelector() {
        return isGB && (
        <Fragment>
          <Question label={translate('interventionForm.control.questionChooseScale')} required={isGB}>
            <SelectCreateContainer>
              <div>
                <Select
                                components={{ Option: ScaleOption }}
                                filterOption={CustomFilter}
                                id="selectScale"
                                isClearable
                                isLoading={isLoading}
                                isSearchable
                                menuPlacement={isMobileOnly ? 'top' : 'bottom'}
                                noOptionsMessage={displayNoScaleAvailable}
                                options={availableScales}
                                placeholder={translate('common.searchByModelBrand')}
                                value={scale || ''}
                                onChange={handleAddScale}
                                onInputChange={value => setSearchInput(value)}
                            />
                {
                                isGB && <RequiredSelectHack value={scale || ''} />
                            }
              </div>
              <div>
                <Media query="(max-width: 768px)">
                  {matches => (matches
                                        ? (
                                          <Fab
                                                color="primary"
                                                size="small"
                                                onClick={() => open({
                                                    type: 'ADD_TOOL',
                                                    onSubmit: handleAddScale,
                                                    defaultValues: {
                                                        ...new EquipmentView(),
                                                        type: {
                                                            value: TOOL_TYPES.SCALE,
                                                            label: translate('common.toolType.SCALE')
                                                        }
                                                    },
                                                    forIntervention: true,
                                                    openSecondaryModal: (options: ModalState) => open(options, true)
                                                })}
                                            >
                                            <FontAwesomeIcon icon={faPlus} />
                                          </Fab>
                                        ) : (
                                          <ButtonBlueLight
                                                data-cy="addScale"
                                                onClick={() => open({
                                                    type: 'ADD_TOOL',
                                                    onSubmit: handleAddScale,
                                                    defaultValues: {
                                                        ...new EquipmentView(),
                                                        type: {
                                                            value: TOOL_TYPES.SCALE,
                                                            label: translate('common.toolType.SCALE')
                                                        }
                                                    },
                                                    forIntervention: true,
                                                    openSecondaryModal: (options: ModalState) => open(options, true)
                                                })}
                                            >
                                            <FontAwesomeIcon icon={faPlus} />
                                            {translate('button.createScale')}
                                          </ButtonBlueLight>
                                        )
                                )}
                </Media>
              </div>
            </SelectCreateContainer>
          </Question>
        </Fragment>
        );
    }

    return (
      <Fragment>
        {!leakDetector
                ? leakDetectorSelector()
                : (
                  <Question label={translate('interventionForm.control.leakDetectorSection')}>
                    <ToolSelection
                            deleteAction={() => {
                                setValue('leakDetector', null);
                                // Forcer un rendu immédiat
                                forceUpdate({});
                            }}
                            editAction={() => open({
                                type: 'ADD_TOOL',
                                onSubmit: handleAddLeakDetector,
                                defaultValues: leakDetector,
                                forIntervention: true,
                                openSecondaryModal: (options: ModalState) => open(options, true)
                            })}
                            tool={leakDetector}
                            toolType={TOOL_TYPES.LEAK_DETECTOR}
                        />
                  </Question>
                )}
        {!scale
                ? scaleSelector()
                : (
                  <Question label={translate('interventionForm.control.scaleSection')}>
                    <ToolSelection
                            deleteAction={() => {
                                setValue('scale', null);
                                forceUpdate({});
                            }}
                            editAction={() => open({
                                type: 'ADD_TOOL',
                                onSubmit: handleAddScale,
                                defaultValues: scale,
                                forIntervention: true,
                                openSecondaryModal: (options: ModalState) => open(options, true)
                            })}
                            tool={scale}
                            toolType={TOOL_TYPES.SCALE}
                        />
                  </Question>
                )}
        <Question color="var(--green)" label={translate('interventionForm.control.questionValidateData')}>
          {!isCanada && (
            <>
              <Translate component="p" text="interventionForm.control.hasLeakDetector" />
              <Controller
                            control={control}
                            name="leakDetectionSystem"
                            render={({ field: { value, onChange } }) => (
                              <ToggleButtonGroup
                                    aria-label={translate('interventionForm.control.hasLeakDetector')}
                                    exclusive
                                    size="medium"
                                    value={value}
                                    onChange={(event, newValue) => {
                                        if (newValue === null) return;
                                        onChange(newValue);
                                        setValue('equipment', {
                                            ...equipment,
                                            hasLeakDetectionSystem: newValue
                                        });
                                    }}
                                >
                                <ToggleButton aria-label={translate('common.yes')} value={true}>{translate('common.yes')}</ToggleButton>
                                <ToggleButton aria-label={translate('common.no')} value={false}>{translate('common.no')}</ToggleButton>
                              </ToggleButtonGroup>
                            )}
                        />
            </>
                )}

          {formValues.circuit?.fluid?.fluidClass && (
            <Fragment>
              <DataContainer>
                {`${translate('interventionForm.control.fluidCategory')}: `}
                <span>{formValues.circuit.fluid.fluidClass}</span>
              </DataContainer>

              <DataContainer>
                {`${translate('interventionForm.control.fluidWeight')}: `}
                <span>
                  {formValues.circuit.fluidCapacity}
                                &nbsp;{translate('common.weightUnit')} -&nbsp;
                  {teqCO2}
                                &nbsp;teqCO2
                </span>
              </DataContainer>

              {!isCanada && (
              <DataContainer>
                {`${translate('interventionForm.control.controlFrequency')}: `}
                <span>{watch('controlFrequency')}</span>
              </DataContainer>
                        )}
            </Fragment>
                )}
        </Question>
      </Fragment>
    );
});
